import { ENTERPRISE_SALES_INTERESTS } from "constants/enterpriseSalesInquiryConfig";
import { PUBLIC_PAGES_COPY } from "copy/publicPages";
import { resolveCopyText } from "i18n/resolveCopy";
import type { IEnterpriseSalesInquiryFormState } from "types/enterprise/enterprise-sales-inquiry-form.types";
import { type LocalizedText } from "types/i18n/LocalizedText";
import { SupportedLocale } from "types/i18n/SupportedLocaleEnum";

/**
 * Builds subject + plain-text body for an enterprise sales mailto draft.
 *
 * @param form - Current inquiry form values
 * @param submissionSourceLabel - Where the user opened the modal (billing vs marketing)
 * @returns Subject line and email body
 */
export function formatEnterpriseSalesInquiryEmail(params: {
  form: IEnterpriseSalesInquiryFormState;
  submissionSourceLabel: string;
}): { subject: string; body: string } {
  const { form, submissionSourceLabel } = params;
  const contactNameLine = form.contactName.trim();
  const workEmailLine = form.workEmail.trim();
  const companyLine = form.companyName.trim();
  // The email goes to the sales team, so its option labels stay English whatever language the visitor reads.
  const inquiryCopy = PUBLIC_PAGES_COPY.enterpriseSalesInquiry;
  const englishLabel = (text: LocalizedText): string =>
    resolveCopyText({ text: text, locale: SupportedLocale.English });

  const selectedInterests = ENTERPRISE_SALES_INTERESTS.filter((interest) => form.interests[interest]).map(
    (interest) => `  • ${englishLabel(inquiryCopy.interest[interest])}`
  );
  const interestsBlock =
    selectedInterests.length > 0 ? selectedInterests.join("\n") : "  • (none selected — see comments if any)";

  const commentsBlock = form.comments.trim() ? form.comments.trim() : "(none)";

  const body = [
    "ENTERPRISE SALES INQUIRY — Muggle Test",
    "",
    `Submitted from: ${submissionSourceLabel}`,
    "",
    "--- Contact ---",
    `Full name: ${contactNameLine}`,
    `Work email: ${workEmailLine}`,
    "",
    "--- Organization ---",
    `Company / organization: ${companyLine}`,
    "",
    "--- Requirements ---",
    `Estimated seats: ${englishLabel(inquiryCopy.seatRange[form.estimatedSeats])}`,
    `Concurrent workflows: ${englishLabel(inquiryCopy.concurrency[form.concurrentWorkflows])}`,
    `Test case scale: ${englishLabel(inquiryCopy.testCaseScale[form.testCaseScale])}`,
    `Timeline: ${englishLabel(inquiryCopy.timeline[form.timeline])}`,
    "",
    "--- Interested in ---",
    interestsBlock,
    "",
    "--- Additional notes ---",
    commentsBlock,
    "",
    "---",
    "Please reply to this email to continue the conversation.",
  ].join("\n");

  const subjectCompany = form.companyName.trim();
  const subject = subjectCompany
    ? `Muggle Test — Enterprise inquiry — ${subjectCompany}`
    : "Muggle Test — Enterprise inquiry";

  return { subject, body };
}

/**
 * Builds a mailto URL with encoded subject and body.
 *
 * @param recipientEmail - Sales inbox
 * @param subject - Email subject
 * @param body - Plain-text body
 * @returns Full mailto href
 */
export function buildEnterpriseSalesInquiryMailto(params: {
  recipientEmail: string;
  subject: string;
  body: string;
}): string {
  const { recipientEmail, subject, body } = params;
  const query = new URLSearchParams({
    subject,
    body,
  });
  return `mailto:${recipientEmail}?${query.toString()}`;
}
